import React, { useEffect } from 'react';

export default function Modal({ open, onClose, title, children, footer, size = 'md' }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const widths = {
    sm: 'max-w-md',
    md: 'max-w-xl',
    lg: 'max-w-3xl',
    xl: 'max-w-5xl',
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className={
          `w-full ${widths[size] || widths.md} bg-surface-panel dark:bg-surface-dark-panel ` +
          'border border-surface-border dark:border-surface-dark-border ' +
          'rounded-thin shadow-thin-lg flex flex-col max-h-[90vh]'
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-surface-border dark:border-surface-dark-border">
          <h2 className="text-base font-semibold">{title}</h2>
          <button
            onClick={onClose}
            className="text-surface-muted hover:text-surface-text dark:hover:text-surface-dark-text"
            aria-label="Close"
          >
            ✕
          </button>
        </div>
        {/* 2026-07-16: overflow-y-auto + overflow-x-hidden — previously
            plain `overflow-auto` allowed horizontal scroll, which then let
            children grow past the modal's max-width instead of wrapping.
            Long text (QA question stems, long form names) rendered on a
            single line with a horizontal scrollbar. This keeps vertical
            scroll available and forces horizontal content to wrap within
            the modal width. */}
        <div className="flex-1 overflow-y-auto overflow-x-hidden p-5">{children}</div>
        {footer && (
          <div className="px-5 py-3 border-t border-surface-border dark:border-surface-dark-border flex justify-end gap-2">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

export function Button({ children, variant = 'default', loading, ...props }) {
  const variants = {
    primary:   'bg-continuum-500 hover:bg-continuum-600 text-white shadow-thin',
    default:   'border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle',
    danger:    'bg-accent-red hover:bg-red-700 text-white shadow-thin',
    ghost:     'hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle',
  };
  return (
    <button
      {...props}
      disabled={loading || props.disabled}
      className={
        'px-3 py-1.5 rounded-thin text-sm font-medium transition-colors ' +
        'disabled:opacity-50 disabled:cursor-not-allowed ' +
        (variants[variant] || variants.default) + ' ' +
        (props.className || '')
      }
    >
      {loading ? '…' : children}
    </button>
  );
}

export function Badge({ children, tone = 'neutral' }) {
  const tones = {
    neutral: 'bg-surface-subtle dark:bg-surface-dark-subtle text-surface-text dark:text-surface-dark-text',
    green:   'bg-green-100 dark:bg-green-900/40 text-green-700 dark:text-green-200',
    amber:   'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-200',
    red:     'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-200',
    blue:    'bg-continuum-50 dark:bg-continuum-900/40 text-continuum-700 dark:text-continuum-200',
    purple:  'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-200',
  };
  return (
    <span className={'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ' + (tones[tone] || tones.neutral)}>
      {children}
    </span>
  );
}
